import { useAppStore } from '@/stores/app'

export const API_BASE = '/api/jackdaw/v1'

// The refresh currently in flight, if any. Every api call goes through
// ensureSession (stores/fetch), so an app load asks for the session a dozen times
// in one tick — the owner's browser was seen making twenty-one at once — and each
// GET /auth/session MINTS A NEW BEARER and retires the one before it (api
// Logic/Auth.go IssueSessionToken). Twenty-one of them leave twenty dead tokens and
// keep whichever RESPONSE landed last, which need not be the one the server kept:
// the app then holds a bearer the api has already forgotten and every call 401s.
//
// Sharing the promise makes a burst one request and one token. It is also what
// makes the retry in stores/fetch safe: the requests that 401 together refresh
// together, and once.
let refreshing: Promise<boolean> | null = null

// ensureSession refreshes the in-memory bearer from the BFF session cookie.
// A cookie-authenticated safe GET (tech spec §8.4) — the one place the cookie
// authenticates. Returns false when there is no live session.
export const ensureSession = async (): Promise<boolean> => {
    if (useAppStore().tokenIsFresh()) return true

    refreshing ??= refreshSession().finally(() => { refreshing = null })

    return refreshing
}

const refreshSession = async (): Promise<boolean> => {
    const appStore = useAppStore()

    try {
        const res = await window.fetch(`${API_BASE}/auth/session`, { credentials: 'same-origin' })
        if (!res.ok) {
            appStore.clearSession()
            return false
        }

        const data = await res.json() as { token: string, expires_at: string }
        appStore.setSession(data.token, data.expires_at)
        return true
    } catch {
        appStore.clearSession()
        return false
    }
}

// startLogin sends the browser into the OAuth flow — the api builds the
// authorize URL and Hydra takes it from there.
export const startLogin = () => {
    window.location.href = `${API_BASE}/auth/start`
}
